import { AfterViewInit, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { ToastrService } from 'ngx-toastr';
import { combineLatest, Observable, Subject, Subscription, takeUntil } from 'rxjs';
import { selectCashieringTransactions, selectRecurringTransactions, selectSubAccountId, selectSubmitCancelTransactionState } from '../../../account-info-header/reducers';
import { AchActions, CheckActions, InternalTransferActions, TransactionsTableActions, WireActions } from '../../../create-request/actions';
import { cancelTransaction, resetSubmitCancelTransactionState } from '../../../create-request/actions/transactions-table-actions';
import { CashieringTransaction, RecurringTransaction } from '../../../create-request/models';
import { selectCurrentTransaction } from '../../../create-request/reducers';
import { TransactionsTableColumn } from '../../types/TransactionTableColumn';
import { transactionDirection, transactionTypes } from '../../utilities/Constants';

@Component({
  standalone: false,
  selector: 'mm-cancel-link-modal',
  templateUrl: './cancel-link-modal.component.html',
  styleUrls: ['./cancel-link-modal.component.scss']
})
export class CancelLinkModalComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() showModal$: Observable<boolean>;
  @Input() id: string;
  destroy$: Subject<boolean> = new Subject<boolean>();
  modal: EverestModal;
  showModalSub: Subscription;
  transactionToCancel: TransactionsTableColumn;
  subAccountId: string;
  currentTransaction: CashieringTransaction | RecurringTransaction;
  defaultTransaction: CashieringTransaction | RecurringTransaction = {
    isSloaSetup: false,
    processDate: '',
    fundsDirection: transactionDirection.out,
    isRecurring: false,
    txnCode: '',
    frequencyCode: ' ',
    to1: '',
    to2: '',
    to3: '',
    isViewable: false,
    isCancelable: false,
    isEditable: false,
    isReactivatable: false,
    cashieringRqstId: '',
    cashieringRqstSecondId: '',
    mediaTypeCd: '',
    typeDescription: '',
    statusCd: '',
    statusCodeDescription: 'Pending',
    bundledSloaIn: false,
    thirdPartyInd: false,
    faMasterAcct: '',
    address: ''
  }

  constructor(private store: Store, private toastr: ToastrService) { }

  ngOnInit(): void {
    this.store.select(selectSubAccountId).pipe(takeUntil(this.destroy$)).subscribe(subAccountId => {
      this.subAccountId = subAccountId
    });

    combineLatest([
      this.store.select(selectCashieringTransactions),
      this.store.select(selectRecurringTransactions),
      this.store.select(selectCurrentTransaction)
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([cashieringTransactions, recurringTransactions, rowData]) => {
        this.transactionToCancel = rowData as TransactionsTableColumn;
        if (this.transactionToCancel.IsActiveRecurring) {
          this.currentTransaction = recurringTransactions.find(tran => tran.cashieringRqstId == this.transactionToCancel.CashieringRqstId) ?? this.defaultTransaction;
        } else {
          this.currentTransaction = cashieringTransactions.find(tran => tran.cashieringRqstId == this.transactionToCancel.CashieringRqstId) ?? this.defaultTransaction;
        }
      });
  }

  ngAfterViewInit(): void {
    this.modal = document.getElementById(this.id) as EverestModal;
    this.showModalSub = this.showModal$.subscribe((show) => {
      if (show) this.modal.showModal();
    })
  }

  ngOnDestroy(): void {
    this.showModalSub?.unsubscribe();
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  onCancelClick(): void {
    this.modal.hideModal();
  }

  onContinueClick(): void {
    this.store.dispatch(resetSubmitCancelTransactionState());
    this.modal.hideModal();
    this.store.dispatch(cancelTransaction(this.transactionToCancel));
    this.store.select(selectSubmitCancelTransactionState).pipe(takeUntil(this.destroy$)).subscribe(submitCancelTransaction => {
      if (submitCancelTransaction.isCancelTransactionCallFinished) {
        if (submitCancelTransaction.isCancelTransactionSubmitted && !submitCancelTransaction.hasCancelTransactionError) {
          this.reloadCurrentPage();
          this.toastr.success("Transaction cancelled");
        } else if (submitCancelTransaction.hasCancelTransactionError) {
          this.toastr.error("Failed to cancel transaction");
        }
        this.store.dispatch(resetSubmitCancelTransactionState());
      }
    })
  }
  reloadCurrentPage(): void {
    // always refresh transaction table after successful cancellation, including on view page as transaction table
    // does not refresh automatically when returning to transaction table from view page
    this.store.dispatch(TransactionsTableActions.refresh({ accountId: this.subAccountId }));
    var el = document.getElementById("view-page");
    if (el !== null) {
      this.refreshViewTransactionPage();
      this.scrollToTop();
    }
  }

  refreshViewTransactionPage(): void {
    // temporary fix while we continue to get these fields from the overview call
    this.currentTransaction = {
      ...this.currentTransaction,
      isEditable: false,
      isCancelable: false
    };
    if (this.transactionToCancel.Type.includes('Check')) {
      this.store.dispatch(CheckActions.callInquiry({
        payload: this.currentTransaction,
        viewType: 'transaction',
        transactionType: transactionTypes.check
      }))
    } else if (this.transactionToCancel.Type.includes('Wire')) {
      this.store.dispatch(WireActions.callInquiry({
        payload: this.currentTransaction,
        viewType: 'transaction',
        transactionType: transactionTypes.wire,
        resourceVersion: '3'
      }));
    } else if (this.transactionToCancel.Type.includes('Internal transfer')) {
      this.store.dispatch(InternalTransferActions.callInquiry({
        payload: this.currentTransaction,
        viewType: 'transaction',
        transactionType: transactionTypes.internalTransfer
      }));
    } else if (this.transactionToCancel.Type.includes('ACH')) {
      this.store.dispatch(AchActions.callInquiry({
        payload: this.currentTransaction,
        viewType: 'transaction',
        transactionType: transactionTypes.ach
      }));
    }
  }

  scrollToTop(): void {
    const elem = document.getElementById('view-page-header');
    setTimeout(() => {
      elem?.focus();
      elem?.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'start' });
    }, 1000);
  }
}

type EverestModal = HTMLElement & {
  showModal: () => {},
  hideModal: () => {}
};
